import { FC, MouseEvent, useEffect, useState } from 'react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { saveProgress } from '../../businessLogicService/practiceProblemsHelper';
import { extractComplexity, PracticeProblemsAssessmentHeader } from './PracticeProblemsAssessmentHeader';
import { PracticeProblemsAssessmentFooter } from './PracticeProblemsAssessmentFooter';
import { getPassedByLevel, getPassedReferences } from './PracticeProblemsExperimentHelpers';
import {
  useLearnosityItemsInitialization,
  type LearnosityInitializationStatus,
  type LearnosityInitializationRefetch,
} from '../../custom-hooks/useLearnosityItemsInitialization';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { PracticeProblemsScCriteriaExperiment } from './PracticeProblemsScCriteriaExperiment';
import { TEAMC_3228_FLAG_VARIANTS } from './constants';
import { ErrorBoundaryUI } from '../../components/ErrorBoundary/ErrorBoundaryUI';

interface Props {
  initObj: LearnosityRequest;
  setPrintDisabled?: (state: boolean) => void;
  setShowRetryButton: (index: boolean) => void;
  skillCode: string;
  handleTakeChallenge: (e: MouseEvent) => void;
  handleReset: () => void;
  showRetryButton: boolean;
  skillId?: string;
  onStatusChange?: (status: LearnosityInitializationStatus) => void;
  onInitError?: () => void;
  refetchInitialization: LearnosityInitializationRefetch;
  /** Shape of `useFeatureFlagVariantKey`'s return value, read once by SkillResource. */
  practiceProblemsExperimentVariant?: string | boolean;
}

export interface PassedByComplexity {
  low: number;
  mid: number;
  high: number;
}

export type FooterStates = 'default' | 'wrongAnswer' | 'correctAnswer';
export const PracticeProblemsAssessmentWrapper: FC<Props> = ({
  initObj,
  setPrintDisabled,

  setShowRetryButton,
  skillCode,
  handleTakeChallenge,
  handleReset,
  showRetryButton,
  skillId,
  onStatusChange,
  onInitError,
  refetchInitialization,
  practiceProblemsExperimentVariant,
}) => {
  const [passedByComplexity, setPassedByComplexity] = useState<PassedByComplexity>({
    low: 0,
    mid: 0,
    high: 0,
  });
  // References of the problems the student has solved. Only written on session restore and on
  // "Check answer", so a dot never gets its check the moment a correct option is picked.
  const [passedReferences, setPassedReferences] = useState<string[]>([]);
  // The Skill Challenge criteria pill and the header's completion checks are treatment-2 only; every
  // other variant sees the plain progress track.
  const showSkillChallengeCriteria = practiceProblemsExperimentVariant === TEAMC_3228_FLAG_VARIANTS.TREATMENT_2;
  const [isOnLastQuestion, setIsOnLastQuestion] = useState(false);
  const userRedux = useAppSelector(selectUser);
  const [enableCheck, setEnableCheck] = useState(true);
  const [currentItemReference, setCurrentItemReference] = useState<string | null>(null);
  const [footerState, setFooterState] = useState<FooterStates>('default');
  const previewMode = usePreviewMode();
  const { status, itemsAppRef } = useLearnosityItemsInitialization({
    initObj,
    restartKey: previewMode ? 'preview' : 'assessment',
    assessmentType: 'practice_problems',
    refetchInitialization,
    onFailure: () => {
      if (itemsAppRef.current) onInitError?.();
    },
    getContainer: () => document.getElementById('print-practice-problems'),
    onReady: itemsApp => {
      const tags = itemsApp?.getTags() || {};
      const scoreByReference = itemsApp?.getItemScores() || {};
      const complexityByReferenceWithScore = Object.keys(tags).map(key => ({
        key,
        complexity: extractComplexity(tags[key]),
        scoring: scoreByReference[key],
      }));

      const { low, mid, high } = getPassedByLevel(complexityByReferenceWithScore);

      setPassedByComplexity({ low: low.length, mid: mid.length, high: high.length });
      // Restores the checks a returning student already earned.
      setPassedReferences(getPassedReferences(complexityByReferenceWithScore));

      if (setPrintDisabled) {
        setPrintDisabled(false);
      }

      const items = itemsApp?.getItems();
      const responses = itemsApp?.getResponses();

      // If there are responses in Learnosity, it means that the user has already started the practice problems, so we show the retry button
      if (Object.keys(responses || {}).length !== 0 && items) {
        setShowRetryButton(true);
      } else {
        setShowRetryButton(false);
      }

      itemsApp?.on('item:load', function () {
        setFooterState('default');
        setEnableCheck(false);
        const currentItem = itemsApp?.getCurrentItem();
        setCurrentItemReference(currentItem?.reference || null);
      });

      const responseIdToReferenceMap: Record<string, string> = {};
      if (items) {
        Object.values(items).forEach(el => {
          el.response_ids?.forEach(responseId => {
            if (responseId) responseIdToReferenceMap[responseId] = el.reference;
          });
        });
        Object.entries(responseIdToReferenceMap).forEach(([responseId, reference]) => {
          itemsApp?.question(responseId).on('changed', function () {
            if (reference) saveProgress(userRedux?.id ?? '', skillCode, reference);
            itemsApp?.save();

            setShowRetryButton(true);
            setEnableCheck(true);
          });
        });
      }
    },
  });

  useEffect(() => {
    setPrintDisabled?.(status !== 'ready');
    onStatusChange?.(status);
  }, [status, setPrintDisabled, onStatusChange]);

  return (
    <div
      className="nc_inline-assessment align-self-center tw-border tw-border-[0.3rem] tw-border-solid tw-border-neutral-100 tw-rounded-[1.25rem]"
      id="print-practice-problems"
    >
      <div className="tw-flex tw-items-center">
        {itemsAppRef.current && (
          <PracticeProblemsAssessmentHeader
            handleReset={handleReset}
            setIsOnLastQuestion={setIsOnLastQuestion}
            currentItemRef={currentItemReference}
            itemsAppRef={itemsAppRef.current}
            passedReferences={passedReferences}
            showCompletionChecks={showSkillChallengeCriteria}
            showRetryButton={showRetryButton}
            skillId={skillId}
          />
        )}
      </div>
      <div>{itemsAppRef.current && showSkillChallengeCriteria && <PracticeProblemsScCriteriaExperiment />}</div>
      <div id="learnosity_assess" className="!tw-relative tw-z-[2] disable-check-answer"></div>
      {status === 'failed' && <ErrorBoundaryUI embedded />}
      {itemsAppRef.current && (
        <PracticeProblemsAssessmentFooter
          isOnLastQuestion={isOnLastQuestion}
          setFooterState={setFooterState}
          footerState={footerState}
          setEnableCheck={setEnableCheck}
          enableCheck={enableCheck}
          itemsAppRef={itemsAppRef.current}
          passedByComplexity={passedByComplexity}
          setPassedByComplexity={setPassedByComplexity}
          setPassedReferences={setPassedReferences}
          handleTakeChallenge={handleTakeChallenge}
        />
      )}
    </div>
  );
};
